<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import { createNewProductNamespace } from "../../utils/textMaintenanceUtils";

const props = defineProps({
    visible: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    "update:visible",
    "products-updated",
    "product-created"
]);

const { t } = useI18n();

// Add Product Dialog state
const newProductId = ref("");
const createBothDomains = ref(true);
const addingProduct = ref(false);

// Computed property for preview of what will be created
const productsToCreate = computed(() => {
    if (!newProductId.value.trim()) return [];
    
    const baseId = newProductId.value.trim().toLowerCase();
    
    if (createBothDomains.value) {
        return [`profile-${baseId}`, `questionnaire-${baseId}`];
    } else {
        return [baseId];
    }
});

const confirmAddProduct = async () => {
    if (!newProductId.value.trim()) return;
    
    try {
        addingProduct.value = true;
        const baseId = newProductId.value.trim().toLowerCase();
        
        console.log(`Creating product: ${baseId} (both domains: ${createBothDomains.value})`);
        
        // Create namespace in German as default
        await createNewProductNamespace(baseId, 'de', createBothDomains.value);
        
        // Emit event to parent to refresh namespace list
        emit("products-updated");
        emit("product-created", baseId);
        
        handleClose();
    } catch (error) {
        console.error('Failed to add product:', error);
    } finally {
        addingProduct.value = false;
    }
};

const handleClose = () => {
    emit("update:visible", false);
    newProductId.value = "";
    createBothDomains.value = true;
    addingProduct.value = false;
};
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="$emit('update:visible', $event)"
        modal
        :header="t('textMaintenance.addProductDialog.title')"
        :style="{ width: '500px' }"
        data-cy="text-maintenance_add-product-dialog"
    >
        <div class="flex flex-col gap-6">
            <!-- Product ID Input -->
            <div>
                <label for="productId" class="block text-sm font-medium mb-1">
                    {{ t('textMaintenance.addProductDialog.productName') }}
                </label>
                <InputText
                    id="productId"
                    v-model="newProductId"
                    :placeholder="t('textMaintenance.addProductDialog.productNamePlaceholder')"
                    class="w-full"
                    maxlength="50"
                    data-cy="text-maintenance_new-product-id-input"
                />
                <small class="text-content-muted mt-1 block">
                    {{ t('textMaintenance.addProductDialog.productNameHint') }}
                </small>
            </div>

            <!-- Create Both Domains Checkbox -->
            <div class="flex items-center gap-2">
                <Checkbox
                    id="createBothDomains"
                    v-model="createBothDomains"
                    :binary="true"
                    data-cy="text-maintenance_create-both-domains-checkbox"
                />
                <label for="createBothDomains" class="text-sm">
                    {{ t('textMaintenance.addProductDialog.createBothDomains') }}
                </label>
            </div>

            <!-- Preview of what will be created -->
            <div v-if="newProductId.trim()" class="p-4 bg-blue-50 border-l-4 border-blue-400">
                <div class="flex">
                    <div class="shrink-0">
                        <i class="pi pi-info-circle text-blue-500"></i>
                    </div>
                    <div class="ml-4">
                        <p class="text-sm text-blue-700 font-medium">
                            {{ t('textMaintenance.addProductDialog.willCreate') }}:
                        </p>
                        <ul class="text-sm text-blue-600 mt-1 list-disc ml-6">
                            <li v-for="product in productsToCreate" :key="product">
                                {{ product }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        
        <template #footer>
            <div class="flex justify-end gap-2">
                <Button
                    :label="t('buttons.cancel')"
                    severity="secondary"
                    @click="handleClose"
                    data-cy="text-maintenance_add-product-cancel-button"
                />
                <Button
                    :label="t('buttons.add')"
                    :disabled="!newProductId.trim()"
                    :loading="addingProduct"
                    @click="confirmAddProduct"
                    data-cy="text-maintenance_add-product-confirm-button"
                />
            </div>
        </template>
    </Dialog>
</template>